<template lang="pug">
v-container
  v-card
    v-card-title.primary--text.pb-0
      v-icon.mr-2(large color="primary") mdi-book-open-page-variant
      | Guía de Marketing Digital para Restaurantes
    
    v-card-subtitle.pt-2 Optimizá tus campañas y aumentá tus ventas
    
    v-card-text
      // Introducción
      .mt-3.mb-5
        v-card.mb-4(flat outlined)
          v-card-title.text-subtitle-1.font-weight-bold
            v-icon.mr-2(color="primary") mdi-information-outline
            | ¿Qué es el seguimiento de campañas?
          v-card-text
            p El seguimiento de campañas te permite medir el rendimiento de tus anuncios digitales. Con Restonline, podés saber exactamente:
            v-row.mt-3
              v-col(cols="12" sm="6" md="3")
                v-card(outlined height="100%")
                  v-card-text.text-center
                    v-icon(x-large color="red") mdi-account-group
                    .subtitle-1.mt-2 Cuántos clientes
                    .caption llegan desde cada anuncio
              v-col(cols="12" sm="6" md="3")
                v-card(outlined height="100%")
                  v-card-text.text-center
                    v-icon(x-large color="orange") mdi-shopping
                    .subtitle-1.mt-2 Qué pedidos
                    .caption genera cada campaña
              v-col(cols="12" sm="6" md="3")
                v-card(outlined height="100%")
                  v-card-text.text-center
                    v-icon(x-large color="green") mdi-cash-multiple
                    .subtitle-1.mt-2 Cuánto facturás
                    .caption por cada inversión
              v-col(cols="12" sm="6" md="3")
                v-card(outlined height="100%")
                  v-card-text.text-center
                    v-icon(x-large color="blue") mdi-chart-line
                    .subtitle-1.mt-2 Qué campañas
                    .caption tienen mejor ROI
      
      // Herramientas disponibles
      h3.text-h5.primary--text.mb-3 Herramientas disponibles
      v-expansion-panels.mb-5(accordion flat hover)
        v-expansion-panel
          v-expansion-panel-header
            .d-flex.align-center
              v-icon.mr-3(color="primary") mdi-chart-line
              span Reportes de Campañas
          v-expansion-panel-content
            p El panel de reportes te muestra el rendimiento de tus campañas con múltiples filtros y opciones de visualización.
            v-row.mb-3
              v-col(cols="12" sm="6")
                .font-weight-bold.mb-2 ¿Qué podés hacer?
                ul.pl-3
                  li Filtrar por fechas específicas
                  li Comparar campañas entre sí
                  li Ver datos por dispositivo (móvil vs. desktop)
                  li Analizar conversiones por canal
              v-col(cols="12" sm="6")
                .font-weight-bold.mb-2 ¿Cómo interpretarlo?
                ul.pl-3
                  li Mayor número de órdenes = campaña efectiva
                  li Mayor valor promedio = segmentación correcta
                  li Más reservas = clientes de mayor valor
                  li Menor costo por adquisición = mejor ROI
            v-alert(dense text type="info") Consejo: revisá tus reportes semanalmente para optimizar tu inversión en marketing.
              
        v-expansion-panel
          v-expansion-panel-header
            .d-flex.align-center
              v-icon.mr-3(color="primary") mdi-link-variant
              span Generador de URLs
          v-expansion-panel-content
            p El generador de URLs te permite crear links especiales para tus campañas que capturan automáticamente datos de conversión.
            .font-weight-bold.mt-2.mb-1 Parámetros UTM importantes:
            v-simple-table.mb-3(dense)
              thead
                tr
                  th Parámetro
                  th ¿Para qué sirve?
                  th Ejemplos
              tbody
                tr
                  td.font-weight-medium UTM Source
                  td De dónde viene el tráfico
                  td instagram, facebook, google
                tr
                  td.font-weight-medium UTM Medium
                  td Qué tipo de medio
                  td social, cpc, email, display
                tr
                  td.font-weight-medium UTM Campaign
                  td Nombre de tu campaña
                  td verano2025, descuentos_marzo
                tr
                  td.font-weight-medium UTM Content
                  td Qué contenido específico
                  td banner, historias, carrousel
                tr
                  td.font-weight-medium UTM Term
                  td Palabras clave (para búsquedas)
                  td pasta, pizza, delivery
            
            .font-weight-bold.mt-2 Ejemplos de URLs para restaurantes cordobeses:
            v-card.mt-1.mb-3(outlined)
              v-card-text
                .caption.mb-1.text-center Promoción de finde en Instagram
                .text-body-2.text-center
                  | turestaurante.com?utm_source=instagram&utm_medium=social&utm_campaign=finde_largo_mayo&utm_content=historias
                  v-btn.ml-2(x-small fab elevation="1" color="secondary" title="Copiar URL")
                    v-icon(small) mdi-content-copy
                    
            v-alert(dense text outlined type="warning") ¡Consejo! Usá nombres de campaña cortos pero descriptivos, y siempre en minúsculas sin espacios ni caracteres especiales.
              
        v-expansion-panel
          v-expansion-panel-header
            .d-flex.align-center
              v-icon.mr-3(color="primary") mdi-google-analytics
              span Configuración de Analytics
          v-expansion-panel-content
            p Conectá tu cuenta de Google Analytics y Facebook Pixel para obtener datos más detallados sobre el comportamiento de tus clientes.
            v-row.mb-3
              v-col(cols="12" sm="6")
                .font-weight-bold.mb-2 Google Analytics
                ul.pl-3
                  li Mide el comportamiento completo de los usuarios
                  li Analiza flujos de navegación y abandonos
                  li Segmenta por ubicación geográfica
                  li Mide tiempo en la página y engagement
              v-col(cols="12" sm="6")
                .font-weight-bold.mb-2 Facebook Pixel
                ul.pl-3
                  li Mide conversiones de anuncios de Facebook
                  li Crea audiencias personalizadas
                  li Optimiza campañas automáticamente
                  li Realiza remarketing a visitantes previos
            
            v-card.pa-3.my-3(outlined)
              v-card-title.subtitle-1 ¿Cómo obtener los códigos de seguimiento?
              
              .font-weight-bold.mt-2 Pasos para Google Analytics:
              ol.pl-3.my-2
                li Creá una cuenta en <a href="https://analytics.google.com" target="_blank">Google Analytics</a>
                li Configurá una nueva propiedad para tu sitio
                li Obtené el código de medición (G-XXXXXXXX)
                li Copialo en la configuración de Analytics de Restonline
              
              .font-weight-bold.mt-2 Pasos para Facebook Pixel:
              ol.pl-3.my-2
                li Ingresá al <a href="https://business.facebook.com/events_manager" target="_blank">Administrador de Eventos de Facebook</a>
                li Creá un nuevo Pixel de Facebook
                li Copiá el ID del Pixel (número de 16 dígitos)
                li Pegalo en la configuración de Analytics de Restonline

      // Estrategias para restaurantes
      h3.text-h5.primary--text.mb-3 Estrategias efectivas para restaurantes cordobeses
      
      v-row
        v-col(cols="12" md="6")
          v-card(outlined)
            v-toolbar(flat dense color="light-blue lighten-5")
              v-toolbar-title.subtitle-1 Estrategias por canal
            v-card-text
              v-row
                v-col(cols="12" sm="6")
                  .font-weight-bold.mb-2.primary--text Instagram
                  ul.pl-3
                    li Fotos de alta calidad de tus platos
                    li Stories destacando especiales del día
                    li Reels mostrando la preparación
                    li Promociones exclusivas por DM
                    
                v-col(cols="12" sm="6")
                  .font-weight-bold.mb-2.primary--text Google Ads
                  ul.pl-3
                    li Anuncios por ubicación geográfica
                    li Palabras clave para platos regionales
                    li Destacar "delivery rápido"
                    li Promocionar menú ejecutivo (mediodía)
                    
          v-card.mt-4(outlined)
            v-toolbar(flat dense color="light-blue lighten-5")
              v-toolbar-title.subtitle-1 Fechas clave en Córdoba
            v-card-text
              v-chip-group(column)
                v-chip(color="pink" text-color="white" small) Día de la Primavera (Sep)
                v-chip(color="green" text-color="white" small) Día del Amigo (Jul)
                v-chip(color="purple" text-color="white" small) Día del Estudiante (Sep)
                v-chip(color="blue" text-color="white" small) Vacaciones de invierno
                v-chip(color="orange" text-color="white" small) Fiestas Patrias
                v-chip(color="red" text-color="white" small) Semana Santa
                v-chip(color="teal" text-color="white" small) Día de la Madre
                v-chip(color="indigo" text-color="white" small) Navidad
          
        v-col(cols="12" md="6")
          v-card(outlined)
            v-toolbar(flat dense color="light-blue lighten-5")
              v-toolbar-title.subtitle-1 Tips de optimización
            v-card-text
              v-timeline(dense)
                v-timeline-item(color="green" small)
                  .font-weight-bold Creá campañas específicas
                  p Segmentá por tipo de cliente: estudiantes, familias, empresarios, etc.
                
                v-timeline-item(color="purple" small)
                  .font-weight-bold Horarios estratégicos
                  p Programá tus anuncios de almuerzo entre 10-12h y de cena entre 18-20h
                
                v-timeline-item(color="orange" small)
                  .font-weight-bold Destacá lo local
                  p Usá términos como "empanadas cordobesas" o "milanesas a la cordobesa"
                
                v-timeline-item(color="blue" small)
                  .font-weight-bold Promocioná eventos
                  p Noches de música en vivo, degustaciones o clases de cocina
              
              v-alert.mt-3(dense color="success" outlined)
                .font-weight-bold.mb-1 Consejo extra
                | Ofrecé un descuento exclusivo solo para quienes lleguen a través de una campaña específica. Esto te ayudará a medir mejor el ROI.
      
      // FAQ
      h3.text-h5.primary--text.mt-5.mb-3 Preguntas frecuentes
      v-expansion-panels(flat)
        v-expansion-panel(v-for="(item, i) in faq" :key="i")
          v-expansion-panel-header {{ item.question }}
          v-expansion-panel-content {{ item.answer }}
              
    v-card-actions
      v-spacer
      v-btn(color="primary" @click="showTutorial")
        v-icon(left) mdi-play-circle-outline
        | Ver tutorial interactivo
</template>

<script>
import MarketingTutorial from '@/components/admin/marketing/MarketingTutorial.vue';

export default {
  name: 'MarketingGuide',
  
  components: {
    MarketingTutorial
  },
  
  data() {
    return {
      tutorialVisible: false,
      faq: [
        {
          question: '¿Qué son los parámetros UTM?',
          answer: 'Los parámetros UTM son etiquetas que se añaden a las URLs para rastrear la efectividad de las campañas de marketing online. Permiten identificar la fuente, medio, campaña y contenido que lleva a un usuario a tu sitio web.'
        },
        {
          question: '¿Puedo usar la misma URL para todas mis campañas?',
          answer: 'No es recomendable. Cada campaña debería tener su propia URL única con parámetros UTM específicos para poder medir correctamente su rendimiento individual.'
        },
        {
          question: '¿Cada cuánto debo revisar los reportes?',
          answer: 'Para restaurantes, lo ideal es revisar los reportes semanalmente y hacer un análisis más profundo mensualmente. Los fines de semana suelen tener patrones diferentes a los días laborables.'
        },
        {
          question: '¿Necesito tener conocimientos técnicos para usar estas herramientas?',
          answer: 'No, las herramientas están diseñadas para ser fáciles de usar sin conocimientos técnicos. El generador de URLs es intuitivo y los reportes muestran la información de forma clara y visual.'
        },
        {
          question: '¿Puedo exportar los datos para analizarlos en Excel?',
          answer: 'Sí, todos los reportes incluyen una opción para exportar los datos en formato CSV o Excel, permitiéndote hacer análisis más personalizados o combinarlos con otros datos de tu negocio.'
        }
      ]
    };
  },
  
  methods: {
    showTutorial() {
      // Resetear el estado del tutorial en localStorage para poder verlo nuevamente
      localStorage.removeItem('marketing_tutorial_shown');
      // Recargar la página para mostrar el tutorial
      this.$router.go();
    }
  }
};
</script>

<style scoped>
/* Mobile first: mejoras para pantallas pequeñas */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1.25rem !important;
  }
  
  .text-h5 {
    font-size: 1.1rem !important;
  }
}
</style>
